import { IconHelpCircle } from '@tabler/icons-react'
import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router'

import { isNavGroup } from '~/app/navLinks'
import { getPageKeyByPath } from '~/app/routes'
import { ReleaseNoteCard } from '~/app/UserSupportDialog/ReleaseNoteCard'
import { UserSupportPopoverMenu } from '~/app/UserSupportDialog/UserSupportPopoverMenu'
import { AstraSidebar, AstraSideNavItem, AstraTinyIndicator, SidebarDivider, SidebarSection } from '~/astra'
import { InboxDrawer } from '~/components/Inbox/InboxDrawer'
import { useFlag } from '~/hooks/useFlag'
import { useNavLinks } from '~/hooks/useNavLinks'
import { useReleaseNote } from '~/hooks/useReleaseNote'
import { useStore } from '~/store/store'
import { analytics } from '~/utils/analytics'

import { ReleaseNotesDialog } from '../UserSupportDialog/ReleaseNotesDialog'
import { CommandPaletteButton } from './CommandPaletteButton'
import { DepartmentSelect } from './DepartmentSelect'
import { InboxButton } from './InboxButton'
import { SpecialtySelect } from './SpecialtySelect'
import { UserMenu } from './UserMenu'

export const Navigation = () => {
    const sidebarRef = useRef<HTMLDivElement>(null)
    const { pathname } = useLocation()
    const { filteredNavLinks } = useNavLinks()
    const { newReleaseNote, markReleaseNoteAsSeen, isReleaseNotesDialogOpen, setIsReleaseNotesDialogOpen } = useReleaseNote()

    const isNavSideBarOpen = useStore(state => state.app.isNavSidebarOpen)
    const setIsNavSidebarOpen = useStore(state => state.app.actions.setIsNavSidebarOpen)
    const setActiveViews = useStore(state => state.app.actions.setActiveViews)
    const useSpecialtiesFilter = useFlag('use_specialties_selection_filter')
    const useSpecialtiesSelection = useFlag('use_specialties_selection')

    useEffect(() => {
        const handleKeyDown = (event: globalThis.KeyboardEvent) => {
            if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'b') {
                event.preventDefault()
                setIsNavSidebarOpen(!isNavSideBarOpen)
            }
        }

        window.addEventListener('keydown', handleKeyDown)
        return () => window.removeEventListener('keydown', handleKeyDown)
    }, [isNavSideBarOpen, setIsNavSidebarOpen])

    // Put location.pathname into the store
    useEffect(() => {
        const pageKey = getPageKeyByPath(pathname)

        if (pageKey) {
            setActiveViews({ [pageKey]: pathname })
            void analytics?.page(pageKey)
        }
    }, [pathname, setActiveViews])

    return (
        <>
            {isReleaseNotesDialogOpen && <ReleaseNotesDialog onClose={() => setIsReleaseNotesDialogOpen(false)} />}

            <AstraSidebar isOpen={isNavSideBarOpen} ref={sidebarRef}>
                <UserMenu />
                {useSpecialtiesFilter && useSpecialtiesSelection ? <SpecialtySelect /> : <DepartmentSelect />}
                <CommandPaletteButton />
                <InboxButton />

                <SidebarDivider />

                {filteredNavLinks.map(navLinks => {
                    if (!isNavGroup(navLinks)) return null

                    return (
                        <SidebarSection key={navLinks.title} title={navLinks.title}>
                            {navLinks.items.map(item => (
                                <AstraSideNavItem key={item.title} icon={item.icon} label={item.title} type="link" to={item.url} />
                            ))}
                        </SidebarSection>
                    )
                })}

                <div className="mt-auto flex w-full flex-col gap-1">
                    {filteredNavLinks.map(navLink => {
                        if (isNavGroup(navLink)) return null

                        return <AstraSideNavItem key={navLink.title} icon={navLink.icon} label={navLink.title} type="link" to={navLink.url} />
                    })}

                    <SidebarDivider />

                    {newReleaseNote !== null && isNavSideBarOpen && (
                        <ReleaseNoteCard
                            description={newReleaseNote.description}
                            onDismiss={markReleaseNoteAsSeen}
                            onClick={() => setIsReleaseNotesDialogOpen(true)}
                        />
                    )}

                    <div className="relative">
                        <UserSupportPopoverMenu>
                            <AstraSideNavItem icon={IconHelpCircle} label="Hjelp" type="action" onClick={() => {}} />
                        </UserSupportPopoverMenu>
                        {!!newReleaseNote && !isNavSideBarOpen && (
                            <AstraTinyIndicator variant="brand" smallOffset placement="top-left" data-test="release-note-indicator" />
                        )}
                    </div>
                </div>
            </AstraSidebar>

            <InboxDrawer sidebarRef={sidebarRef} />
        </>
    )
}
